import { Grid, InputText, Span } from "@goodfill/ui-components";
import { FieldGroup } from "../../../../../../form/FieldGroup";
import { PhoneInput } from "../../../../../phoneInput/PhoneInput";
import { Controller, DefaultValues, useForm } from "react-hook-form";
import { step1SignUpState, ValidStep1SignUpState, validStep1State } from "./step1SignUpState";
import { StepSubmit } from "../StepSubmit";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRecoilState } from "recoil";
import { deserializePhone, serializePhone } from "../../../../../../form/zod/formPhone";

interface Props {
  defaultStep1State: DefaultValues<ValidStep1SignUpState>;
  onSuccess: () => void;
  isLoading: boolean;
}

export const Step1SignUp: React.FC<Props> = ({ defaultStep1State, onSuccess, isLoading }) => {
  const [, setValidStep1] = useRecoilState(validStep1State);

  const { control, trigger, handleSubmit } = useForm<ValidStep1SignUpState>({
    defaultValues: defaultStep1State,
    mode: "onTouched",
    resolver: zodResolver(step1SignUpState),
  });

  const onSubmit = async () => {
    const isStepValid = await trigger(undefined, { shouldFocus: true });

    if (isStepValid) {
      handleSubmit((v) => {
        setValidStep1(v);
      })();
      onSuccess();
    }
  };

  const areAllDisabled = isLoading;

  return (
    <>
      <div className="flex flex-col px-4 md:px-8 py-4 md:py-8 items-center border-b border-neutral-110">
        <Span size="2xl" uppercase={false} font="semibold">
          Hi Welcome!
        </Span>
        <Span className="Steps_welcomeMessage" wrap={true}>
          To open a free account and start you investing safely, we need to know more about you. Let's get started!
        </Span>

        <Grid cols={2} gap={0.75} className="w-full">
          <Controller
            render={({ field, fieldState: { error } }) => (
              <FieldGroup
                label="First Name"
                validation={[error?.message ?? ""]}
                className="w-80-sm w-full-after-md"
                fill
              >
                <InputText
                  setValue={field.onChange}
                  border={error === undefined ? "neutral" : "red"}
                  placeholder="Your First Name"
                  value={field.value ?? ""}
                  onBlur={field.onBlur}
                  ref={field.ref}
                  id={"firstName"}
                  disabled={areAllDisabled}
                />
              </FieldGroup>
            )}
            name="firstName"
            control={control}
          />

          <Controller
            render={({ field, fieldState: { error } }) => (
              <FieldGroup
                label="Last Name"
                validation={[error?.message ?? ""]}
                className="w-80-sm w-full-after-md"
                fill
              >
                <InputText
                  setValue={field.onChange}
                  border={error === undefined ? "neutral" : "red"}
                  placeholder="Your Last Name"
                  value={field.value ?? ""}
                  onBlur={field.onBlur}
                  ref={field.ref}
                  id={"lastName"}
                  disabled={areAllDisabled}
                />
              </FieldGroup>
            )}
            name="lastName"
            control={control}
          />

          <Controller
            render={({ field, fieldState: { error } }) => {
              const [countryCallCode, nationalNumber] = deserializePhone(field.value);

              return (
                <FieldGroup
                  label="Phone Number"
                  validation={[error?.message ?? ""]}
                  className="w-80-sm w-full-after-md"
                  fill
                >
                  <PhoneInput
                    onChange={(value) => field.onChange(serializePhone(value.countryCallCode, value.nationalNumber))}
                    onBlur={field.onBlur}
                    isValid={error === undefined}
                    value={{ countryCallCode, nationalNumber }}
                    ref={field.ref}
                    id={"phone1"}
                    disabled={true} // Intentionally always disabled
                  />
                </FieldGroup>
              );
            }}
            name="phone1"
            control={control}
          />

          <Controller
            render={({ field, fieldState: { error } }) => (
              <FieldGroup label="Email" validation={[error?.message ?? ""]} className="w-80-sm w-full-after-md" fill>
                <InputText
                  setValue={field.onChange}
                  border={error === undefined ? "neutral" : "red"}
                  placeholder="Your email address"
                  value={field.value ?? ""}
                  onBlur={field.onBlur}
                  ref={field.ref}
                  id={"email"}
                  disabled={true} // Intentionally always disabled
                />
              </FieldGroup>
            )}
            name="email"
            control={control}
          />
        </Grid>
      </div>

      <StepSubmit text="Continue" isLoading={isLoading} onClick={onSubmit} />
    </>
  );
};
